import {FormInstance} from 'antd/es/form';
import {useCallback, useState, useRef, useMemo, useEffect} from 'react';

import {validateRowFull, TEditableColumn, IValidationController} from 'components/editable-table';
import {TEntity} from 'modules/data';
import {TStateSetter} from 'shared/types/generics';

/**
 * Хук для управления валидацией строк в редактируемой таблице/форме
 *
 * @param form - Экземпляр формы
 * @param formListName - Имя Form.List
 * @param columns - Массив столбцов с конфигурацией валидации
 * @param setValidationContext - Callback для передачи методов валидации в контекст
 * @returns Объект с методами и состояниями для управления валидацией строк
 *
 * @remarks
 * Хук предоставляет:
 * - Валидацию отдельных строк и всех строк одновременно
 * - Управление состоянием валидации (загрузка, ошибки)
 * - Методы для очистки ошибок
 * - Интеграцию с родительскими компонентами через контекст
 *
 * @see validateRowFull - Функция полной валидации строки
 * @see validateFieldValue - Функция валидации отдельного поля
 */
export const useRowValidation = (
    form: FormInstance | undefined,
    formListName: string,
    columns: TEditableColumn[],
    setValidationContext?: TStateSetter<IValidationController | undefined>,
) => {
    const errorsRef = useRef<Record<number, Record<string, string[]>>>({});
    const validatingRef = useRef<Set<number>>(new Set());

    // Состояние только для триггера ререндеров
    const [updateTrigger, setUpdateTrigger] = useState(0);

    // Функция для обновления UI
    const forceUpdate = useCallback(() => {
        setUpdateTrigger(prev => prev + 1);
    }, []);

    const getFieldErrors = useCallback((rowIndex: number, fieldName: string): string[] => (
        errorsRef.current[rowIndex]?.[fieldName] || []
    ), []);

    const getAllFieldErrors = useCallback(() => (
        Object.values(errorsRef.current)
    ), []);

    // Проверка статуса валидации строки
    const isRowValidating = useCallback((rowIndex: number): boolean => (
        validatingRef.current.has(rowIndex)
    ), []);

    const validateRow = useCallback(async (rowIndex: number, rowData: TEntity): Promise<Record<string, string[]>> => {
        // Добавляем строку в валидацию
        validatingRef.current.add(rowIndex);
        forceUpdate();

        try {
            const errors = await validateRowFull(
                rowData,
                rowIndex,
                form,
                formListName,
                columns,
            );

            // Обновляем ref с ошибками
            errorsRef.current = {
                ...errorsRef.current,
                [rowIndex]: errors
            };

            return errors;
        } catch (error) {
            console.error('Ошибка валидации:', error);
            return {};
        } finally {
            validatingRef.current.delete(rowIndex);
            forceUpdate();
        }
    }, [form, formListName, columns, forceUpdate]);

    const validateAllRows = useCallback(async (data?: TEntity[]): Promise<boolean> => {
        let allValid = true;
        if (!data?.length) return allValid;
        const validationPromises = data?.map((row, index) =>
            validateRow(index, row).then(errors => {
                if (Object.keys(errors).length > 0) {
                    allValid = false;
                }
            })
        );

        await Promise.all(validationPromises);
        return allValid;
    }, [validateRow]);

    const clearRowErrors = useCallback((rowIndex?: number) => {
        if (rowIndex !== undefined) {
            delete errorsRef.current[rowIndex];
        } else {
            errorsRef.current = {};
        }
        forceUpdate();
    }, [forceUpdate]);

    const hasRowErrors = useCallback((rowIndex: number): boolean => (
        !!errorsRef.current[rowIndex] && Object.keys(errorsRef.current[rowIndex]).length > 0
    ), []);

    useEffect(() => {
        if (setValidationContext) {
            setValidationContext({
                validateAllRows,
                getAllFieldErrors,
            });
        }
    }, [setValidationContext, validateAllRows, clearRowErrors]);

    return useMemo(() => ({
        validateRow,
        validateAllRows,
        clearRowErrors,
        getFieldErrors,
        getAllFieldErrors,
        isRowValidating,
        hasRowErrors,
        hasErrors: Object.keys(errorsRef.current).length > 0,
        // Для отладки
        _errorsRef: errorsRef,
        updateTrigger,
    }), [validateRow, validateAllRows, clearRowErrors, getFieldErrors, isRowValidating, hasRowErrors]);
};
